import { makeAutoObservable } from "mobx";
import { InvoiceRequest, InvoiceRecord, ProcessInvoiceRequest } from "../../models/InvoiceManagement";
import { InvoiceApi } from "../../apis/Invoice";

export class InvoiceManagementPageModel {
    // 数据状态
    unprocessedRequests: InvoiceRequest[] = [];
    processedInvoices: InvoiceRecord[] = [];
    filteredProcessedInvoices: InvoiceRecord[] = [];
    
    // UI状态
    activeTab: string = "unprocessed";
    loading: boolean = false;
    searchKeyword: string = "";
    
    // 详情弹窗状态
    detailModalVisible: boolean = false;
    selectedInvoice: InvoiceRequest | InvoiceRecord | null = null;
    orderListModalVisible: boolean = false;
    
    // 开票处理弹窗状态
    processModalVisible: boolean = false;
    processingInvoiceId: string = "";
    invoiceNo: string = "";

    constructor() {
        makeAutoObservable(this);
    }

    // 设置活跃标签页
    setActiveTab = (tab: string) => {
        this.activeTab = tab;
        if (tab === "unprocessed") {
            this.loadUnprocessedRequests();
        } else {
            this.loadProcessedInvoices();
        }
    };

    // 加载待开票记录
    loadUnprocessedRequests = async () => {
        this.loading = true;
        try {
            const data = await InvoiceApi.getUnprocessedRequests();
            this.unprocessedRequests = data;
        } catch (error) {
            console.error("加载待开票记录失败:", error);
        } finally {
            this.loading = false;
        }
    };

    // 加载已开票记录
    loadProcessedInvoices = async () => {
        this.loading = true;
        try {
            const data = await InvoiceApi.getProcessedInvoices();
            this.processedInvoices = data;
            this.filteredProcessedInvoices = data;
        } catch (error) {
            console.error("加载已开票记录失败:", error);
        } finally {
            this.loading = false;
        }
    };

    // 搜索已开票记录
    searchProcessedInvoices = () => {
        if (!this.searchKeyword.trim()) {
            this.filteredProcessedInvoices = this.processedInvoices;
            return;
        }
        
        const keyword = this.searchKeyword.toLowerCase();
        this.filteredProcessedInvoices = this.processedInvoices.filter(invoice =>
            (invoice.buyerId || "").toLowerCase().includes(keyword) ||
            (invoice.buyerName || "").toLowerCase().includes(keyword) ||
            (invoice.title || "").toLowerCase().includes(keyword) ||
            (invoice.taxNumber || "").toLowerCase().includes(keyword)
        );
    };

    // 设置搜索关键词
    setSearchKeyword = (keyword: string) => {
        this.searchKeyword = keyword;
    };

    // 显示详情弹窗
    showDetailModal = (invoice: InvoiceRequest | InvoiceRecord) => {
        this.selectedInvoice = invoice;
        this.detailModalVisible = true;
    };

    // 从详情弹窗直接开票处理
    processInvoiceFromDetail = () => {
        if (this.selectedInvoice) {
            this.showProcessModal(this.selectedInvoice.invoiceRequestId || this.selectedInvoice.id || "");
        }
    };

    // 隐藏详情弹窗
    hideDetailModal = () => {
        this.detailModalVisible = false;
        this.selectedInvoice = null;
    };

    // 显示订单列表弹窗
    showOrderListModal = () => {
        this.orderListModalVisible = true;
    };

    // 隐藏订单列表弹窗
    hideOrderListModal = () => {
        this.orderListModalVisible = false;
    };

    // 显示开票处理弹窗
    showProcessModal = (invoiceId: string) => {
        this.processingInvoiceId = invoiceId;
        this.processModalVisible = true;
        this.invoiceNo = "";
    };

    // 隐藏开票处理弹窗
    hideProcessModal = () => {
        this.processModalVisible = false;
        this.processingInvoiceId = "";
        this.invoiceNo = "";
    };

    // 设置发票号码
    setInvoiceNo = (invoiceNo: string) => {
        this.invoiceNo = invoiceNo;
    };

    // 处理开票
    processInvoice = async () => {
        if (!this.invoiceNo.trim()) {
            return false;
        }

        try {
            const params: ProcessInvoiceRequest = {
                invoiceRequestId: this.processingInvoiceId,
                invoiceNo: this.invoiceNo.trim()
            };
            
            const success = await InvoiceApi.processInvoice(params);
            if (success) {
                this.hideProcessModal();
                this.loadUnprocessedRequests();
                return true;
            }
            return false;
        } catch (error) {
            console.error("开票处理失败:", error);
            return false;
        }
    };

    // 复制文本到剪贴板
    copyToClipboard = async (text: string): Promise<boolean> => {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (error) {
            console.error("复制失败:", error);
            return false;
        }
    };
} 